import { callApi } from './api';
import Order, { AccessoryOrderStatus } from '@/models/order';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Admin orders CRM — all under `/accessories/admin/orders` (admin-role gated).
const accessoriesAdminApiUrl = import.meta.env.VITE_API_URL_V1 + '/accessories/admin';

export interface OrdersListResult {
    orders: Order[];
    total: number;
    page: number;
    limit: number;
}

export interface OrdersQuery {
    page: number;
    limit: number;
    status?: string;
    search?: string;
}

export const getOrders = async (params: OrdersQuery) => {
    const q = new URLSearchParams();
    q.set('page', String(params.page));
    q.set('limit', String(params.limit));
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    return callApi<OrdersListResult>({ url: `${accessoriesAdminApiUrl}/orders?${q.toString()}`, method: 'GET' });
};

export const useOrders = (params: OrdersQuery) => {
    return useQuery({ queryKey: ['orders', params], queryFn: () => getOrders(params) });
};

export const getOrder = async (orderId: string) => {
    return callApi<Order>({ url: `${accessoriesAdminApiUrl}/orders/${orderId}`, method: 'GET' });
};

export const useOrder = (orderId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['order', orderId],
        queryFn: () => getOrder(orderId),
        enabled: !!orderId && isOpen,
    });
};

export type OrderUpdatePayload = {
    status?: AccessoryOrderStatus;
    trackingNumber?: string;
    carrier?: string;
    trackingUrl?: string;
    adminNote?: string;
    /**
     * Internal triage flag. The API rejects raising it without a note on the
     * order (existing or in this same payload).
     */
    needsAttention?: boolean;
    /**
     * Editable because a comped or off-platform order arrives with NO address by
     * construction — Stripe never collected one — and without this such an order
     * is unshippable forever. Amounts, line items, Stripe ids, email and
     * orderNumber stay immutable server-side.
     */
    shippingAddress?: {
        line1: string;
        line2?: string;
        city: string;
        state?: string;
        postal_code: string;
        country: string;
    };
    shippingName?: string;
    phone?: string;
};

export const updateOrder = async ({ orderId, ...payload }: OrderUpdatePayload & { orderId: string }) => {
    return callApi<Order>({ url: `${accessoriesAdminApiUrl}/orders/${orderId}`, method: 'PUT', data: payload });
};

export const useUpdateOrder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateOrder,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['order', variables.orderId] });
        },
    });
};

/**
 * A manual tracking update — the interim while carrier auto-tracking is dormant.
 *
 * Writes the SAME fields the automated poll writes, so the customer's order
 * tracker and the Course 33 board panel render it identically whether a person
 * or a carrier supplied it. `notify` is opt-in per update: most parcel
 * movements do not deserve an interruption.
 */
export type TrackingUpdatePayload = {
    statusDetail: string;
    status?: 'pending' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception';
    location?: string;
    notify?: boolean;
};

export const postTrackingUpdate = async ({ orderId, ...payload }: TrackingUpdatePayload & { orderId: string }) => {
    return callApi<{ order: Order; notifications?: { channel: string; outcome: string; reason?: string }[] }>({
        url: `${accessoriesAdminApiUrl}/orders/${orderId}/tracking-update`,
        method: 'POST',
        data: payload,
    });
};

export const usePostTrackingUpdate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: postTrackingUpdate,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['order', variables.orderId] });
            // The Course CRM reads the same order document, so its list and
            // drawer must refetch too or they show a stale timeline.
            queryClient.invalidateQueries({ queryKey: ['course-crm-clients'] });
            queryClient.invalidateQueries({ queryKey: ['course-crm-client'] });
        },
    });
};

export const deleteOrder = async ({ orderId }: { orderId: string }) => {
    return callApi({ url: `${accessoriesAdminApiUrl}/orders/${orderId}`, method: 'DELETE' });
};

export const useDeleteOrder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteOrder,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
    });
};

/** Shared money formatter for the orders UI (amounts are in the smallest unit). */
export const formatMoney = (amount: number, currency: string): string => {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((amount || 0) / 100);
    } catch {
        return `${((amount || 0) / 100).toFixed(2)} ${(currency || 'usd').toUpperCase()}`;
    }
};
